/* =========================================================================
   LandingPage10 — the "build your own online system" page.

   This one is deliberately NOT another revision of 6→7→8→9. Those pages all
   run the same Hormozi sales flow and sell a course that teaches you to build
   an app. LandingPage10 sells the OUTCOME — an online system you own — and
   leads with the mission instead of the offer:

     hero → mission → what a system is → the foundation → how it works →
     rent vs. hire vs. own → final CTA

   No price card, no value stack, no guarantee. Both CTAs go to /subscriptions,
   where the price already lives, so this page can stay about the mission. That
   also means it needs no ENROLLMENT_OPEN branch — it never promises a seat.

   COPY is 100% its own lp10.* namespace in BOTH locales (en + es), with no
   fallback chain to lp6. The positioning here is different enough that a
   half-inherited page would say two things at once; and unlike LandingPage9,
   there is no "Spanish keeps the lp6 original" case to preserve. A key missing
   from one locale therefore renders as a raw "lp10.x" — the two blocks are
   kept in lockstep, and lp10Copy.test.js enforces that.

   Everything is scoped under .lp10 so it never collides with the Argon globals
   or any other landing sheet. LandingPage6–9 are untouched.
   ========================================================================= */

import React, { useEffect } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage10.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faAddressBook,
  faCreditCard,
  faUserLock,
  faCubes,
  faRobot,
  faHeadphones,
  faGlobe,
  faLightbulb,
  faWandMagicSparkles,
  faRocket,
  faKey,
  faArrowsRotate,
  faUserTie,
  faInfinity,
  faUsers,
  faDatabase,
  faWindowMaximize,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// What an "online system" actually is, in four things people already recognise
// from running their own thing. Rendered two-up, not four-across.
const SYSTEM_PARTS = [
  { n: 1, icon: faCalendarCheck, tone: "lp10-ic-cyan" },
  { n: 2, icon: faAddressBook, tone: "lp10-ic-blue" },
  { n: 3, icon: faCreditCard, tone: "lp10-ic-green" },
  { n: 4, icon: faUserLock, tone: "lp10-ic-purple" },
];

// "We give people the tools and foundation" — the mission's middle clause,
// made concrete: a project to start from, the AI, the tutor, a real address.
const FOUNDATION = [
  { n: 1, icon: faCubes, tone: "lp10-ic-cyan" },
  { n: 2, icon: faRobot, tone: "lp10-ic-purple" },
  { n: 3, icon: faHeadphones, tone: "lp10-ic-blue" },
  { n: 4, icon: faGlobe, tone: "lp10-ic-green" },
];

// Idea → build → publish → it stays yours.
const STEPS = [
  { n: 1, icon: faLightbulb },
  { n: 2, icon: faWandMagicSparkles },
  { n: 3, icon: faRocket },
  { n: 4, icon: faKey },
];

// Rent forever / pay an engineer every time / own it. The two losing options
// carry a cross, the third a check.
const CHOICES = [
  { n: 1, icon: faArrowsRotate, win: false },
  { n: 2, icon: faUserTie, win: false },
  { n: 3, icon: faInfinity, win: true },
];

const LandingPage10 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp10-active");
    const onScroll = () => {
      document.body.classList.toggle("lp10-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp10-active", "lp10-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goEnroll = () => navigate("/subscriptions");
  const scrollToHow = () => {
    const el = document.getElementById("lp10-how");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp10">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp10-hero">
          <span className="lp10-orb lp10-orb-1" />
          <span className="lp10-orb lp10-orb-2" />
          <span className="lp10-orb lp10-orb-3" />

          <Container>
            <Row>
              <Col lg="7">
                <span className="lp10-eyebrow">
                  <span className="dot" />
                  {t("lp10.eyebrow")}
                </span>
                <h1 className="lp10-title">{t("lp10.heroTitle")}</h1>
                <p className="lp10-lead">{t("lp10.heroLead")}</p>

                <div className="lp10-actions">
                  <button className="lp10-btn-primary" onClick={goEnroll}>
                    {t("lp10.heroPrimary")}
                  </button>
                  <button className="lp10-btn-ghost" onClick={scrollToHow}>
                    {t("lp10.heroSecondary")}
                  </button>
                </div>

                <div className="lp10-pills">
                  <span className="lp10-pill">{t("lp10.pill1")}</span>
                  <span className="lp10-pill">{t("lp10.pill2")}</span>
                  <span className="lp10-pill">{t("lp10.pill3")}</span>
                </div>
              </Col>

              {/* The system, drawn: clients → app → data, and it's live. This
                  replaces the AI-chat panel LandingPage6–9 put here, because on
                  this page the thing being sold is the system, not the tutor. */}
              <Col lg="5">
                <div className="lp10-schema-wrap">
                  <div className="lp10-schema">
                    <div className="lp10-schema-head">
                      <span className="lp10-schema-title">
                        {t("lp10.diagramTitle")}
                      </span>
                      <span className="lp10-live">
                        <span className="lp10-live-dot" />
                        {t("lp10.diagramLive")}
                      </span>
                    </div>

                    <div className="lp10-node">
                      <span className="lp10-node-ic">
                        <FontAwesomeIcon icon={faUsers} />
                      </span>
                      <span className="lp10-node-text">
                        <span className="lp10-node-label">
                          {t("lp10.diagramUsers")}
                        </span>
                      </span>
                    </div>

                    <div className="lp10-node lp10-node-app">
                      <span className="lp10-node-ic">
                        <FontAwesomeIcon icon={faWindowMaximize} />
                      </span>
                      <span className="lp10-node-text">
                        <span className="lp10-node-label">
                          {t("lp10.diagramApp")}
                        </span>
                        <span className="lp10-node-bars">
                          <span
                            className="lp10-node-bar cyan"
                            style={{ width: "34%" }}
                          />
                          <span
                            className="lp10-node-bar purple"
                            style={{ width: "22%" }}
                          />
                          <span
                            className="lp10-node-bar mute"
                            style={{ width: "28%" }}
                          />
                        </span>
                      </span>
                    </div>

                    <div className="lp10-node">
                      <span className="lp10-node-ic">
                        <FontAwesomeIcon icon={faDatabase} />
                      </span>
                      <span className="lp10-node-text">
                        <span className="lp10-node-label">
                          {t("lp10.diagramData")}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ---------------------------- MISSION ---------------------------- */}
        {/* The reason the rest of the page exists. Nothing to click here on
            purpose — it's the one quiet beat between the hero and the pitch. */}
        <section className="lp10-mission lp10-grid-bg">
          <Container>
            <div className="lp10-mission-inner">
              <span className="lp10-eyebrow-2">{t("lp10.missionEyebrow")}</span>
              <div className="lp10-mission-rule" />
              <p>{t("lp10.missionBody")}</p>
            </div>
          </Container>
        </section>

        {/* --------------- WHAT AN ONLINE SYSTEM ACTUALLY IS ---------------- */}
        <section className="lp10-what">
          <Container>
            <div className="lp10-section-head">
              <span className="lp10-eyebrow-2">{t("lp10.whatEyebrow")}</span>
              <h2>{t("lp10.whatTitle")}</h2>
              <p>{t("lp10.whatSubtitle")}</p>
            </div>
            <Row>
              {SYSTEM_PARTS.map((p) => (
                <Col lg="6" className="lp10-col" key={p.n}>
                  <div className="lp10-what-card">
                    <div className={`lp10-card-icon ${p.tone}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <div>
                      <h6>{t(`lp10.what${p.n}T`)}</h6>
                      <p>{t(`lp10.what${p.n}P`)}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* -------------------------- FOUNDATION --------------------------- */}
        <section className="lp10-foundation lp10-grid-bg">
          <Container>
            <div className="lp10-section-head">
              <span className="lp10-eyebrow-2">
                {t("lp10.foundationEyebrow")}
              </span>
              <h2>{t("lp10.foundationTitle")}</h2>
              <p>{t("lp10.foundationSubtitle")}</p>
            </div>
            <Row>
              {FOUNDATION.map((f) => (
                <Col lg="3" md="6" className="lp10-col" key={f.n}>
                  <div className="lp10-found-card">
                    <div className={`lp10-card-icon ${f.tone}`}>
                      <FontAwesomeIcon icon={f.icon} />
                    </div>
                    <h6>{t(`lp10.foundation${f.n}T`)}</h6>
                    <p>{t(`lp10.foundation${f.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- HOW IT WORKS -------------------------- */}
        <section className="lp10-how" id="lp10-how">
          <Container>
            <div className="lp10-section-head">
              <h2>{t("lp10.howTitle")}</h2>
              <p>{t("lp10.howSubtitle")}</p>
            </div>
            <Row className="lp10-how-rail">
              {STEPS.map((s, i) => (
                <Col lg="3" md="6" className="lp10-col" key={s.n}>
                  <div className="lp10-step">
                    <span className="lp10-step-num">{i + 1}</span>
                    <h6>{t(`lp10.how${s.n}T`)}</h6>
                    <p>{t(`lp10.how${s.n}P`)}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- WHY YOUR OWN -------------------------- */}
        {/* Rent / hire / own. This is the page's only money talk, and it stays
            about the alternatives — the course price lives on /subscriptions. */}
        <section className="lp10-why">
          <Container>
            <div className="lp10-section-head lp10-head-dark">
              <span className="lp10-eyebrow-2 lp10-eyebrow-light">
                {t("lp10.whyEyebrow")}
              </span>
              <h2>{t("lp10.whyTitle")}</h2>
              <p>{t("lp10.whySubtitle")}</p>
            </div>

            <Row className="lp10-why-grid">
              {CHOICES.map((c) => (
                <Col lg="4" md="6" className="lp10-col" key={c.n}>
                  <div className={`lp10-why-card${c.win ? " win" : ""}`}>
                    <span
                      className={`lp10-why-mark ${c.win ? "check" : "cross"}`}
                    >
                      <FontAwesomeIcon icon={c.win ? faCheck : faXmark} />
                    </span>
                    <div className="lp10-why-ic">
                      <FontAwesomeIcon icon={c.icon} />
                    </div>
                    <h6>{t(`lp10.why${c.n}Label`)}</h6>
                    <div className="lp10-why-cost">{t(`lp10.why${c.n}Cost`)}</div>
                    <p>{t(`lp10.why${c.n}Note`)}</p>
                  </div>
                </Col>
              ))}
            </Row>

            <p className="lp10-why-punch">{t("lp10.whyPunch")}</p>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA --------------------------- */}
        <section className="lp10-cta">
          <Container>
            <div className="lp10-cta-card">
              <Row>
                <Col lg="8">
                  <h3>{t("lp10.finalTitle")}</h3>
                  <p>{t("lp10.finalBody")}</p>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp10-cta-btn" onClick={goEnroll}>
                    {t("lp10.finalCta")}
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage10;
